import React, { useState } from 'react';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { useSubscriptionStore } from '../../store/subscriptionStore';
import { Button } from '../ui/Button';
import { ConfirmationModal } from '../ui/ConfirmationModal';
import { 
  Crown, 
  Settings, 
  AlertTriangle,
  Clock,
  Gift,
  ExternalLink
} from 'lucide-react';
import { SubscriptionTier, SubscriptionSource } from '../../types';

interface SubscriptionCardProps {
  onUpgrade?: () => void;
  onViewPlans?: () => void;
}

export const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  onUpgrade,
  onViewPlans
}) => {
  const {
    currentTier,
    hasActiveSubscription,
    isInTrial,
    isInGracePeriod,
    trialDaysRemaining,
    gracePeriodDaysRemaining,
    getStatusText,
    getTierDisplayName
  } = useFeatureAccess();

  const {
    subscription,
    openCustomerPortal,
    cancelSubscription,
    isCancelingSubscription
  } = useSubscriptionStore();

  const [showCancelModal, setShowCancelModal] = useState(false);
  const isAppleLocked = subscription?.source === SubscriptionSource.Apple;

  const handleCancel = async () => {
    const success = await cancelSubscription({ cancelAtPeriodEnd: true });
    if (success) {
      setShowCancelModal(false);
    }
  };

  const getStatusIcon = () => {
    if (isInTrial) return <Gift className="w-5 h-5 text-blue-600" />;
    if (isInGracePeriod) return <Clock className="w-5 h-5 text-orange-600" />;
    if (hasActiveSubscription) return <Crown className="w-5 h-5 text-green-600" />;
    return <AlertTriangle className="w-5 h-5 text-gray-600" />;
  };

  const getStatusColor = () => {
    if (isInTrial) return 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800';
    if (isInGracePeriod) return 'bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-800';
    if (hasActiveSubscription) return 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800';
    return 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-600';
  };

  const getTierBadge = () => {
    const tierName = getTierDisplayName();
    const badgeColor = currentTier === SubscriptionTier.Pro 
      ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      : currentTier === SubscriptionTier.Basic
      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';

    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${badgeColor}`}>
        {tierName}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <>
      <div className={`p-6 rounded-lg border ${getStatusColor()}`}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            {getStatusIcon()}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Subscription
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {getStatusText()}
              </p>
            </div>
          </div>
          {getTierBadge()}
        </div>

        {/* Trial Information */}
        {isInTrial && trialDaysRemaining !== null && (
          <div className="mb-4 p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <div className="flex items-center space-x-2">
              <Gift className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                Free Trial: {trialDaysRemaining} day{trialDaysRemaining === 1 ? '' : 's'} remaining
              </span>
            </div>
            <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
              Upgrade now to continue enjoying all features after your trial ends.
            </p>
          </div>
        )}

        {/* Grace Period Information */}
        {isInGracePeriod && gracePeriodDaysRemaining !== null && (
          <div className="mb-4 p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4 text-orange-600 dark:text-orange-400" />
              <span className="text-sm font-medium text-orange-800 dark:text-orange-200">
                Grace Period: {gracePeriodDaysRemaining} day{gracePeriodDaysRemaining === 1 ? '' : 's'} remaining
              </span>
            </div>
            <p className="text-xs text-orange-700 dark:text-orange-300 mt-1">
              Subscribe now to keep your data and continue using Koru.
            </p>
          </div>
        )}

        {/* Subscription Details */}
        {subscription && hasActiveSubscription && (
          <div className="space-y-2 mb-4">
            {subscription.currentPeriodEnd && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Next billing:</span>
                <span className="text-gray-900 dark:text-gray-100">
                  {formatDate(subscription.currentPeriodEnd)}
                </span>
              </div>
            )}
            
            {subscription.cancelAtPeriodEnd && (
              <div className="flex items-center space-x-2 text-sm text-orange-600 dark:text-orange-400">
                <AlertTriangle className="w-4 h-4" />
                <span>Subscription will cancel on {formatDate(subscription.currentPeriodEnd)}</span>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2">
          {isAppleLocked ? (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950 w-full">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Your subscription is managed through the iOS App Store. To upgrade, downgrade, or cancel, open the Koru app on your iPhone.
              </p>
            </div>
          ) : (
            <>
              {!hasActiveSubscription && !isInTrial && !isInGracePeriod && (
                <Button
                  onClick={onUpgrade || onViewPlans}
                  variant="primary"
                  className="flex-1"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Subscribe Now
                </Button>
              )}

              {(isInTrial || isInGracePeriod) && (
                <Button
                  onClick={onUpgrade || onViewPlans}
                  variant="primary"
                  className="flex-1"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Upgrade Now
                </Button>
              )}

              {hasActiveSubscription && !subscription?.cancelAtPeriodEnd && (
                <>
                  <Button
                    onClick={onViewPlans}
                    variant="outline"
                    className="flex-1"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Change Plan
                  </Button>

                  <Button
                    onClick={() => openCustomerPortal()}
                    variant="outline"
                    className="flex-1"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Manage
                  </Button>

                  <Button
                    onClick={() => setShowCancelModal(true)}
                    variant="outline"
                    className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20 border-red-200 dark:border-red-800"
                  >
                    Cancel
                  </Button>
                </>
              )}

              {hasActiveSubscription && subscription?.cancelAtPeriodEnd && (
                <Button
                  onClick={() => openCustomerPortal()}
                  variant="outline"
                  className="flex-1"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Reactivate
                </Button>
              )}
            </>
          )}
        </div>

        {/* Billing Features Summary */}
        {(isInTrial || isInGracePeriod || hasActiveSubscription) && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Current plan includes:
            </p>
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-400">
              <div>✓ Unlimited habits</div>
              <div>✓ Progress tracking</div>
              {(currentTier === SubscriptionTier.Basic || currentTier === SubscriptionTier.Pro) && (
                <>
                  <div>✓ Data export</div>
                  <div>✓ Email support</div>
                </>
              )}
              {currentTier === SubscriptionTier.Pro && (
                <>
                  <div>✓ Advanced analytics</div>
                  <div>✓ Priority support</div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Cancel Confirmation Modal */}
      <ConfirmationModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancel}
        title="Cancel Subscription"
        message="Are you sure you want to cancel your subscription? You'll continue to have access until the end of your current billing period."
        confirmText="Cancel Subscription"
        cancelText="Keep Subscription"
        variant="warning"
        isLoading={isCancelingSubscription}
      />
    </>
  );
}; 